import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import queryString from "query-string";
import moment from "moment";
import { useSelector } from "react-redux";
import { FiEdit2, FiClock, FiX, FiArrowUp, FiArrowDown } from "react-icons/fi";
import { FiDownload } from "react-icons/fi";
import {
    getSupplierStockAlerts,
    updatePromiseDate,
    updateAction,
    getCategories,
    getActions,
    getAgents,
    getReasons,
    updateReason,
    updateNote,
    downloadStockAlerts,
    getStockAlertHistory,
    refreshStockAlerts
} from "../../../core/services/stockAlertService";
import Loader from "../Loader/Loader";
import CommonPagination from "../CommonPagination/CommonPagination";

// @@ AI-COMMENT: Custom Multi-Select with checkboxes
const MultiSelectCheckbox = ({ options, selectedValues, onChange, label, placeholder }: any) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleOption = (option: string) => {
        if (selectedValues.includes(option)) {
            onChange(selectedValues.filter((v: string) => v !== option));
        } else {
            onChange([...selectedValues, option]);
        }
    };

    const filteredOptions = options.filter((opt: string) =>
        opt.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        // @@ AI-COMMENT: Styled to match the shared CMS filter-control look (h-8 / text-xs / rounded-sm)
        <div className="relative" ref={dropdownRef}>
            <label className="block text-[11px] text-gray-600 mb-1">{label}</label>
            <div
                className="bg-white border border-gray-300 h-8 rounded-sm px-1.5 text-xs cursor-pointer flex justify-between items-center hover:border-blue-400"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="truncate flex-1 text-[#3F3F3F]">
                    {selectedValues.length === 0 ? <span className="text-gray-400">{placeholder}</span> :
                        selectedValues.length === 1 ? selectedValues[0] : `${selectedValues.length} selected`}
                </span>
                <span className="text-gray-400 text-[9px] ml-1">
                    {isOpen ? '▲' : '▼'}
                </span>
            </div>
            {isOpen && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-300 rounded-sm shadow-lg z-50 overflow-hidden w-full">
                    {/* @@ AI-COMMENT: Sticky search input for rapid filtering */}
                    <div className="p-1.5 border-b border-gray-200 bg-[#f7f7f7]">
                        <input
                            type="text"
                            placeholder="Search..."
                            className="w-full border border-gray-300 h-7 rounded-sm px-1.5 text-xs focus:outline-none focus:border-blue-400"
                            value={searchTerm}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt: string, i: number) => (
                                <div
                                    key={i}
                                    className="flex items-center px-2 py-1.5 hover:bg-[#f7f7f7] cursor-pointer"
                                    onClick={() => toggleOption(opt)}
                                >
                                    <input
                                        type="checkbox"
                                        className="mr-2 cursor-pointer h-3 w-3 text-blue-500 border-gray-300 rounded-sm focus:ring-blue-400"
                                        checked={selectedValues.includes(opt)}
                                        onChange={() => { }}
                                    />
                                    <span className="text-xs text-[#3F3F3F] truncate select-none" title={opt}>{opt}</span>
                                </div>
                            ))
                        ) : (
                            <div className="px-2 py-3 text-xs text-gray-500 italic text-center">No results found...</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// @@ AI-COMMENT: Stock Alert main list and table logic
const StockAlertList = () => {
    const { user } = useSelector((s: any) => s.authStore);
    const userEmail = user?.email || user?.preferred_username || user?.upn || user?.unique_name || "";

    const [loading, setLoading] = useState(false);
    // @@ AI-COMMENT: Track download-in-progress state
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);

    const [categories, setCategories] = useState<string[]>([]);
    const [actionOptions, setActionOptions] = useState<string[]>([]);
    const [agentOptions, setAgentOptions] = useState<string[]>([]);
    const [reasonOptions, setReasonOptions] = useState<string[]>([]);

    // @@ AI-COMMENT: Filter and sort state for the listing API; overdue null = all, true = overdue, false = upcoming
    const [filters, setFilters] = useState<{
        msn: string;
        productName: string;
        category: string[];
        supplierId: string;
        agent: string[];
        action: string[];
        reason: string[];
        startDate: string;
        endDate: string;
        page: number;
        pageSize: number;
        sortBy: string;
        sortDir: string;
        overdue: boolean | null;
    }>({
        msn: "",
        productName: "",
        category: [],
        supplierId: "",
        agent: [],
        action: [],
        reason: [],
        startDate: "",
        endDate: "",
        page: 1,
        pageSize: 10,
        sortBy: "",
        sortDir: "",
        overdue: null
    });


    const [editingDateMsn, setEditingDateMsn] = useState<string | null>(null);
    const [editingDateValue, setEditingDateValue] = useState("");

    const [editingNoteMsn, setEditingNoteMsn] = useState<string | null>(null);
    const [editingNoteValue, setEditingNoteValue] = useState("");

    // @@ AI-COMMENT: State for MSN history modal
    const [historyMsn, setHistoryMsn] = useState<string | null>(null);
    const [historyData, setHistoryData] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    useEffect(() => {
        fetchMetadata();
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchData();
        }, 500);
        return () => clearTimeout(handler);
    }, [filters]);

    const fetchMetadata = async () => {
        try {
            const [catRes, actRes, agtRes, reasonRes] = await Promise.all([getCategories(), getActions(), getAgents(), getReasons()]);
            if (catRes?.data?.status) setCategories(catRes.data.data || []);
            if (actRes?.data?.status) setActionOptions(actRes.data.data || []);
            if (agtRes?.data?.status) setAgentOptions(agtRes.data.data || []);
            if (reasonRes?.data?.status) setReasonOptions(reasonRes.data.data || []);
        } catch (error) {
            console.error("Error fetching metadata", error);
        }
    };

    // @@ AI-COMMENT: Build JSON payload for listing and download APIs
    const buildPayload = (isDownload = false) => {
        const payload: any = { oos: true };

        if (!isDownload) {
            payload.page = filters.page;
            payload.pageSize = filters.pageSize;
        }

        if (filters.msn && filters.msn.trim() !== "") {
            payload.msnList = filters.msn.split(',').map(s => s.trim()).filter(Boolean);
        }
        if (filters.productName && filters.productName.trim() !== "") {
            payload.productName = filters.productName.trim();
        }

        if (filters.category && filters.category.length > 0) payload.categoryList = filters.category;
        if (filters.supplierId && filters.supplierId.trim() !== "") {
            payload.supplierIdList = filters.supplierId.split(',').map(s => s.trim()).filter(Boolean);
        }
        if (filters.agent && filters.agent.length > 0) payload.agentList = filters.agent;
        if (filters.action && filters.action.length > 0) payload.actionList = filters.action;
        if (filters.reason && filters.reason.length > 0) payload.reasonList = filters.reason;
        if (filters.startDate) payload.startDate = filters.startDate;
        if (filters.endDate) payload.endDate = filters.endDate;
        if (filters.sortBy) payload.sortBy = filters.sortBy;
        if (filters.sortDir) payload.sortDir = filters.sortDir;
        if (filters.overdue !== null && filters.overdue !== undefined) payload.overdue = filters.overdue;

        return payload;
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const payload = buildPayload(false);
            const response = await getSupplierStockAlerts(payload);
            if (response?.data?.status) {
                setData(response.data.data || []);
                setTotalCount(response.data.totalCount || 0);
            } else {
                setData([]);
                setTotalCount(0);
            }
        } catch (error) {
            toast.error("Failed to load alerts");
            setData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    // @@ AI-COMMENT: Build filter payload without pagination and trigger CSV download
    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            const payload = buildPayload(true);
            const response = await downloadStockAlerts(payload);
            const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `stock-alerts-${moment().format("YYYY-MM-DD")}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("CSV downloaded successfully");
        } catch (error) {
            toast.error("Failed to download CSV");
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleFilterChange = (key: string, value: any) => {
        setFilters((prev) => {
            const newFilters = { ...prev, [key]: value };
            if (key !== "page") {
                newFilters.page = 1;
            }
            return newFilters;
        });
    };

    const resetFilters = () => {
        setFilters({
            msn: "",
            productName: "",
            category: [],
            supplierId: "",
            agent: [],
            action: [],
            reason: [],
            startDate: "",
            endDate: "",
            page: 1,
            pageSize: 10,
            sortBy: "",
            sortDir: "",
            overdue: null
        });
    };

    // @@ AI-COMMENT: Toggle sort column and direction; same column cycles asc -> desc -> off
    const handleSort = (column: "updatedAt" | "orderCount") => {
        setFilters((prev) => {
            if (prev.sortBy !== column) {
                return { ...prev, sortBy: column, sortDir: "desc", page: 1 };
            }
            if (prev.sortDir === "desc") {
                return { ...prev, sortDir: "asc", page: 1 };
            }
            return { ...prev, sortBy: "", sortDir: "", page: 1 };
        });
    };

    // @@ AI-COMMENT: Render the appropriate sort indicator icon for a column header (sits on the dark table head)
    const SortIcon = ({ column }: { column: string }) => {
        if (filters.sortBy !== column) return <span className="ml-1 text-gray-500 text-[10px]">↕</span>;
        if (filters.sortDir === "asc") return <FiArrowUp size={12} className="ml-1 text-white" />;
        return <FiArrowDown size={12} className="ml-1 text-white" />;
    };

    // @@ AI-COMMENT: True when any filter differs from its default, used to enable the Clear Filters button
    const hasActiveFilters = Boolean(
        filters.msn || filters.productName || filters.supplierId ||
        filters.category.length || filters.agent.length || filters.action.length || filters.reason.length ||
        filters.startDate || filters.endDate || filters.overdue !== null || filters.sortBy
    );

    const handleActionChange = async (msn: string, newAction: string) => {
        try {
            const res = await updateAction({ msn, updatedBy: userEmail, action: newAction });
            if (res.data?.status || res.data?.statusCode === 200) {
                toast.success("Action updated successfully");
                fetchData();
            } else {
                toast.error("Failed to update action");
            }
        } catch (error) {
            toast.error("Failed to update action");
        }
    };

    const handleReasonChange = async (msn: string, newReason: string) => {
        try {
            const res = await updateReason({ msn, updatedBy: userEmail, reason: newReason });
            if (res.data?.status || res.data?.statusCode === 200) {
                toast.success("Reason updated successfully");
                fetchData();
            } else {
                toast.error("Failed to update reason");
            }
        } catch (error) {
            toast.error("Failed to update reason");
        }
    };

    const savePromiseDate = async (msn: string) => {
        if (!editingDateValue) {
            setEditingDateMsn(null);
            return;
        }
        try {
            // @@ AI-COMMENT: Format date properly for the API before sending
            const formattedDate = moment(editingDateValue).format("YYYY-MM-DD");
            const res = await updatePromiseDate({ msn, updatedBy: userEmail, promiseDate: formattedDate });
            if (res.data?.status || res.data?.statusCode === 200) {
                toast.success("Promise date updated");
                fetchData();
            } else {
                toast.error("Failed to update date");
            }
        } catch (error) {
            toast.error("Failed to update date");
        }
        setEditingDateMsn(null);
    };

    const saveNote = async (msn: string) => {
        if (!editingNoteValue || !editingNoteValue.trim()) {
            setEditingNoteMsn(null);
            return;
        }
        try {
            const res = await updateNote({ msn, updatedBy: userEmail, notes: editingNoteValue });
            if (res.data?.status || res.data?.statusCode === 200) {
                toast.success("Note updated successfully");
                fetchData();
            } else {
                toast.error("Failed to update note");
            }
        } catch (error) {
            toast.error("Failed to update note");
        }
        setEditingNoteMsn(null);
    };

    const renderDate = (timestamp: number) => {
        if (!timestamp) return "-";
        return moment(timestamp).format("DD MMM YYYY HH:mm");
    };

    // @@ AI-COMMENT: Fetch and open the history modal for a given MSN
    const openHistory = async (msn: string) => {
        setHistoryMsn(msn);
        setHistoryData([]);
        setHistoryLoading(true);
        try {
            const res = await getStockAlertHistory(msn);
            if (res?.data?.status) {
                setHistoryData(res.data.data || []);
            } else {
                toast.error("Failed to load history");
            }
        } catch (error) {
            toast.error("Failed to load history");
        } finally {
            setHistoryLoading(false);
        }
    };

    // @@ AI-COMMENT: Handle manual refresh of stock alerts
    const handleRefresh = async () => {
        setLoading(true);
        try {
            const res = await refreshStockAlerts();
            if (res.data?.status || res.data?.statusCode === 200) {
                toast.success("Alert data refreshed successfully");
                fetchData();
            } else {
                toast.error("Failed to refresh data");
            }
        } catch (error) {
            toast.error("Failed to refresh data");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* @@ AI-COMMENT: Page header matching the shared CMS listing header (title left, primary action right) */}
            <div className="p-4 flex items-center">
                <div>
                    <p className="text-xl font-bold text-[#43425D]">SUPPLIER STOCK ALERTS</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">Overdue &amp; upcoming restock actions across inventory clusters.</p>
                </div>
                {/* @@ AI-COMMENT: Refresh Button */}
                <button
                    onClick={handleRefresh}
                    disabled={loading}
                    title="Re-build alert data from the latest inventory snapshot"
                    className="h-9 py-2 px-4 ml-auto bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
                >
                    {loading ? (
                        <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Refreshing...
                        </>
                    ) : (
                        <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh Data
                        </>
                    )}
                </button>
            </div>

            {/* @@ AI-COMMENT: Filter strip matching the shared CMS listing filter bar */}
            <div className="mt-2.5 bg-white px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3">
                <div className="w-[200px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Msn(s)</label>
                    <input
                        type="text"
                        placeholder="Comma separated MSNs"
                        className="border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full"
                        value={filters.msn}
                        onChange={(e) => handleFilterChange("msn", e.target.value)}
                    />
                </div>
                {/* @@ AI-COMMENT: Separate Product Name search filter input */}
                <div className="w-[180px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Product Name</label>
                    <input
                        type="text"
                        placeholder="Search product name"
                        className="border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full"
                        value={filters.productName}
                        onChange={(e) => handleFilterChange("productName", e.target.value)}
                    />
                </div>
                {/* @@ AI-COMMENT: Supplier ID search filter input supporting multiple separated by comma */}
                <div className="w-[180px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Supplier</label>
                    <input
                        type="text"
                        placeholder="Comma separated supplier IDs"
                        className="border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full"
                        value={filters.supplierId}
                        onChange={(e) => handleFilterChange("supplierId", e.target.value)}
                    />
                </div>
                <div className="w-[160px]">
                    <MultiSelectCheckbox
                        label="Category"
                        placeholder="All Categories"
                        options={categories}
                        selectedValues={filters.category}
                        onChange={(val: string[]) => handleFilterChange("category", val)}
                    />
                </div>
                <div className="w-[160px]">
                    <MultiSelectCheckbox
                        label="Assigned Agent"
                        placeholder="All Agents"
                        options={agentOptions}
                        selectedValues={filters.agent}
                        onChange={(val: string[]) => handleFilterChange("agent", val)}
                    />
                </div>
                <div className="w-[160px]">
                    <MultiSelectCheckbox
                        label="Action"
                        placeholder="All Actions"
                        options={actionOptions}
                        selectedValues={filters.action}
                        onChange={(val: string[]) => handleFilterChange("action", val)}
                    />
                </div>
                <div className="w-[160px]">
                    <MultiSelectCheckbox
                        label="Reason"
                        placeholder="All Reasons"
                        options={reasonOptions}
                        selectedValues={filters.reason}
                        onChange={(val: string[]) => handleFilterChange("reason", val)}
                    />
                </div>
                {/* @@ AI-COMMENT: Overdue filter dropdown: null=All, true=Overdue, false=Upcoming */}
                <div className="w-[120px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Due</label>
                    <select
                        className="bg-white border border-gray-300 h-8 w-full focus:outline-none focus:border-blue-400 px-1.5 rounded-sm text-xs"
                        value={filters.overdue === null ? "" : String(filters.overdue)}
                        onChange={(e) => {
                            const val = e.target.value;
                            handleFilterChange("overdue", val === "" ? null : val === "true");
                        }}
                    >
                        <option value="">All</option>
                        <option value="true">Overdue</option>
                        <option value="false">Upcoming</option>
                    </select>
                </div>
                {/* @@ AI-COMMENT: Date range filter inputs bound to startDate and endDate params */}
                <div className="w-[140px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Start Date</label>
                    <input
                        type="date"
                        className="border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange("startDate", e.target.value)}
                    />
                </div>
                <div className="w-[140px]">
                    <label className="block text-[11px] text-gray-600 mb-1">End Date</label>
                    <input
                        type="date"
                        className="border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full"
                        value={filters.endDate}
                        min={filters.startDate || undefined}
                        onChange={(e) => handleFilterChange("endDate", e.target.value)}
                    />
                </div>
                <button
                    onClick={resetFilters}
                    disabled={!hasActiveFilters}
                    className="h-8 px-4 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Clear Filters
                </button>
                {/* @@ AI-COMMENT: Download CSV exports every row matching the applied filters */}
                <button
                    onClick={handleDownload}
                    disabled={downloadLoading}
                    title="Download all rows matching the applied filters"
                    className="h-8 px-4 ml-auto rounded text-xs font-medium border border-blue-500 text-blue-600 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                    <FiDownload size={13} />
                    {downloadLoading ? "Downloading..." : "Download CSV"}
                </button>
            </div>

            {/* @@ AI-COMMENT: Listing table using the shared CMS table chrome (dark head, zebra rows) */}
            <div className="mt-2.5 campaign-list bg-white mb-[54px] relative">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1280px]">
                        <thead className="bg-[#343434]">
                            <tr>
                                {/* @@ AI-COMMENT: Removed standalone MSN column. "Product Name" becomes "Product Info" and houses MSN. */}
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[20%]">Product Info</th>
                                {/* @@ AI-COMMENT: Sortable Orders column header */}
                                <th
                                    className="px-4 py-3 text-center text-xs font-semibold text-white w-[5%] cursor-pointer select-none hover:text-blue-300"
                                    onClick={() => handleSort("orderCount")}
                                >
                                    <span className="inline-flex items-center justify-center">
                                        Orders <SortIcon column="orderCount" />
                                    </span>
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[8%]">Supplier</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-white w-[5%]">Due</th>
                                {/* @@ AI-COMMENT: Dates column — Updated At sub-field is sortable */}
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold text-white w-[13%] cursor-pointer select-none hover:text-blue-300"
                                    onClick={() => handleSort("updatedAt")}
                                >
                                    <span className="inline-flex items-center">
                                        Dates <SortIcon column="updatedAt" />
                                    </span>
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[16%]">Notes</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Reason</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Action</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[7%]">Agent</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[8%]">Updated By</th>
                                {/* @@ AI-COMMENT: History column triggers the per-MSN changelog modal */}
                                <th className="px-4 py-3 text-center text-xs font-semibold text-white w-[4%]">History</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && data.length === 0 ? (
                                <tr>
                                    {/* @@ AI-COMMENT: Adjusted colSpan for loader empty state */}
                                    <td colSpan={11} className="px-4 py-8 text-center text-xs text-gray-500">
                                        <Loader />
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    {/* @@ AI-COMMENT: Adjusted colSpan for empty state after merging columns */}
                                    <td colSpan={11} className="px-4 py-8 text-center text-xs text-gray-500">
                                        No stock alerts found.
                                    </td>
                                </tr>
                            ) : (
                                data.map((item, index) => (
                                    // @@ AI-COMMENT: Zebra striping per CMS table style; overdue rows keep a red tint so they stay scannable
                                    <tr key={item.id || index} className={
                                        typeof item.overdue === 'number' && item.overdue > 0
                                            ? "bg-[#FEF2F2] hover:bg-[#FEE2E2]"
                                            : "[&:nth-child(odd)]:bg-[#f7f7f7] hover:bg-[#eef4fb]"
                                    }>
                                        {/* @@ AI-COMMENT: MSN links to productUrl in a new tab if available */}
                                        {/* @@ AI-COMMENT: MSN moved inside Product Name column */}
                                        <td className="px-4 py-3 text-left text-xs text-[#3F3F3F] align-top max-w-[240px]" title={item.productName} >
                                            <div className="font-semibold text-blue-600 mb-1">
                                                {item.productUrl ? (
                                                    <a
                                                        href={item.productUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="hover:text-blue-800 hover:underline inline-block"
                                                    >
                                                        {item.msn}
                                                    </a>
                                                ) : (
                                                    <span className="inline-block">{item.msn}</span>
                                                )}
                                            </div>
                                            <div className="font-semibold truncate">{item.productName}</div>
                                            <div className="text-[10px] text-gray-500 mt-0.5 truncate">{item.category}</div>
                                        </td>
                                        <td className="px-4 py-3 text-center text-xs text-[#3F3F3F] font-semibold align-top">
                                            {item.orderCount || 0}
                                        </td>
                                        <td className="px-4 py-3 text-left text-xs text-[#3F3F3F] align-top truncate max-w-[100px]" title={item.supplierId}>
                                            {item.supplierId}
                                        </td>
                                        {/* @@ AI-COMMENT: Overdue rendered as a pill, matching the pill treatment used on other CMS listings */}
                                        <td className="px-4 py-3 text-center text-xs align-top">
                                            {typeof item.overdue === 'number' ? (
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${item.overdue > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                                    {item.overdue}d
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        {/* @@ AI-COMMENT: Consolidated dates column with Promise Date first for quick access */}
                                        <td className="px-4 py-3 text-left text-xs text-[#3F3F3F] align-top space-y-1.5">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-gray-500">Promise Date</span>
                                                {editingDateMsn === item.msn ? (
                                                    <input
                                                        autoFocus
                                                        type="date"
                                                        // @@ AI-COMMENT: Restrict past dates from being selected as promise date
                                                        min={moment().format("YYYY-MM-DD")}
                                                        className="border border-gray-300 h-7 rounded-sm px-1.5 text-xs w-full mt-0.5 focus:outline-none focus:border-blue-400"
                                                        value={editingDateValue}
                                                        onChange={(e) => setEditingDateValue(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && savePromiseDate(item.msn)}
                                                        onBlur={() => savePromiseDate(item.msn)}
                                                    />
                                                ) : (
                                                    <button
                                                        type="button"
                                                        title="Click to edit promise date"
                                                        className="inline-flex items-center gap-1.5 mt-0.5 px-1.5 py-0.5 rounded-sm border border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 w-max"
                                                        onClick={() => { setEditingDateMsn(item.msn); setEditingDateValue(item.promiseDate ? moment(item.promiseDate).format('YYYY-MM-DD') : ""); }}
                                                    >
                                                        <span className="font-medium text-[#3F3F3F] text-xs leading-tight">{renderDate(item.promiseDate)}</span>
                                                        {/* @@ AI-COMMENT: Always-visible pencil icon signals this field is editable */}
                                                        <FiEdit2 className="text-blue-500 flex-shrink-0" size={11} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-gray-500">OOS Date</span>
                                                <span className="font-medium text-[#3F3F3F]">{renderDate(item.oosDate)}</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-gray-500">Updated At</span>
                                                <span className="font-medium text-[#3F3F3F]">{item.updatedAt ? moment(item.updatedAt).format("DD MMM YYYY HH:mm") : "-"}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-left text-xs text-[#3F3F3F] align-top">
                                            {editingNoteMsn === item.msn ? (
                                                <textarea
                                                    autoFocus
                                                    rows={4}
                                                    className="border border-gray-300 rounded-sm px-1.5 py-1 text-xs w-full focus:outline-none focus:border-blue-400 resize-none overflow-y-auto"
                                                    value={editingNoteValue}
                                                    onChange={(e) => setEditingNoteValue(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            saveNote(item.msn);
                                                        }
                                                    }}
                                                    onBlur={() => saveNote(item.msn)}
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    title={item.notes || "Click to add a note"}
                                                    className="flex items-start gap-1.5 text-left w-full px-1.5 py-1 rounded-sm border border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100"
                                                    onClick={() => { setEditingNoteMsn(item.msn); setEditingNoteValue(item.notes || ""); }}
                                                >
                                                    <span className="whitespace-pre-wrap line-clamp-2 flex-1 text-xs text-[#3F3F3F]">{item.notes || <span className="text-gray-400 italic">Add note...</span>}</span>
                                                    {/* @@ AI-COMMENT: Always-visible pencil icon signals this field is editable */}
                                                    <FiEdit2 className="text-blue-500 flex-shrink-0 mt-0.5" size={11} />
                                                </button>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-left text-xs align-top">
                                            <select
                                                className={`border h-8 rounded-sm px-1.5 text-xs w-full focus:outline-none focus:border-blue-400 ${item.reason ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-300 text-[#3F3F3F]"
                                                    }`}
                                                value={item.reason || ""}
                                                onChange={(e) => handleReasonChange(item.msn, e.target.value)}
                                            >
                                                <option value="" disabled>Reason</option>
                                                {reasonOptions.map((opt, i) => (
                                                    <option key={i} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-4 py-3 text-left text-xs align-top">
                                            <select
                                                className={`border h-8 rounded-sm px-1.5 text-xs w-full focus:outline-none focus:border-blue-400 ${item.action ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-300 text-[#3F3F3F]"
                                                    }`}
                                                value={item.action || ""}
                                                onChange={(e) => handleActionChange(item.msn, e.target.value)}
                                            >
                                                <option value="" disabled>Action</option>
                                                {actionOptions.map((opt, i) => (
                                                    <option key={i} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-4 py-3 text-left text-xs text-[#3F3F3F] font-semibold align-top">
                                            {/* @@ AI-COMMENT: Display Agent without update dropdown */}
                                            {item.agent || "-"}
                                        </td>
                                        {/* @@ AI-COMMENT: Removed standalone Updated At column block */}
                                        <td className="px-4 py-3 text-left text-[10px] text-gray-500 align-top max-w-[120px] truncate" title={item.updatedBy}>
                                            {item.updatedBy || "-"}
                                        </td>
                                        {/* @@ AI-COMMENT: History button cell opens the changelog modal for this MSN */}
                                        <td className="px-4 py-3 text-center align-top">
                                            <button
                                                onClick={() => openHistory(item.msn)}
                                                className="inline-flex items-center justify-center p-1.5 rounded-sm text-blue-600 hover:bg-blue-50"
                                                title="View history"
                                            >
                                                <FiClock size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {
                    loading && data.length > 0 && (
                        <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
                            <Loader />
                        </div>
                    )
                }
            </div >

            {/* @@ AI-COMMENT: Shared CMS pagination replaces the bespoke pager for consistency across listings */}
            {totalCount > 0 && (
                <CommonPagination
                    data={{ totalCount }}
                    PAGE_SIZE={filters.pageSize}
                    onPageChange={(pageNumber: number) => handleFilterChange("page", pageNumber)}
                />
            )}

            {/* @@ AI-COMMENT: Slide-in history modal overlay */}
            {
                historyMsn && (
                    <div className="fixed inset-0 z-50 flex justify-end">
                        <div className="absolute inset-0 bg-black/40" onClick={() => setHistoryMsn(null)} />
                        <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between px-5 py-4 bg-[#43425D]">
                                <div>
                                    <p className="text-base font-bold text-white">Change History</p>
                                    <p className="text-[11px] text-gray-300 mt-0.5">MSN: <span className="font-semibold text-white">{historyMsn}</span></p>
                                </div>
                                <button onClick={() => setHistoryMsn(null)} className="p-1.5 rounded-sm text-white hover:bg-white/10">
                                    <FiX size={18} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="flex-1 overflow-y-auto px-5 py-4">
                                {historyLoading ? (
                                    <div className="flex items-center justify-center h-40">
                                        <Loader />
                                    </div>
                                ) : historyData.length === 0 ? (
                                    <p className="text-xs text-gray-400 text-center mt-16">No history found for this MSN.</p>
                                ) : (
                                    <ol className="relative border-l border-gray-300 ml-3 space-y-6">
                                        {historyData.map((entry, i) => (
                                            <li key={entry.id || i} className="ml-5">
                                                {/* @@ AI-COMMENT: Timeline dot for each history entry */}
                                                <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 bg-[#43425D] rounded-full ring-4 ring-white">
                                                    <span className="w-1.5 h-1.5 bg-white rounded-full" />
                                                </span>
                                                <div className="bg-[#f7f7f7] border border-gray-200 rounded-sm p-3">
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <span className="px-2 py-0.5 rounded-full bg-[#43425D] text-white text-[10px] font-semibold">{entry.field}</span>
                                                        <span className="text-[10px] text-gray-500">{entry.updatedAt ? moment(entry.updatedAt).format("DD MMM YYYY HH:mm") : "-"}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs">
                                                        <span className="text-gray-400 line-through">{entry.oldValue ?? "—"}</span>
                                                        <span className="text-gray-400">→</span>
                                                        <span className="font-semibold text-[#3F3F3F]">{entry.newValue ?? "—"}</span>
                                                    </div>
                                                    <p className="text-[10px] text-gray-500 mt-1.5">by <span className="font-medium text-gray-700">{entry.updatedBy || "system"}</span></p>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }
        </>
    );
};

export default StockAlertList;
